"use client";

import ClickSpark from "@repo/ui/components/react-bits/click-spark";
import { useShowCursorStyleControls } from "@shared/hooks/use-pointer-fine";
import { useCursorPreference } from "@shared/lib/cursor-preference-context";
import { useEffect } from "react";
import { CursorMessagePortalMount } from "./cursor-message";
import { SmoothCursor } from "./smooth-cursor";

/**
 * When `useShowCursorStyleControls` is true (desktop-like input, not a mobile-class device per
 * Client Hints when available): may mount {@link SmoothCursor}, message portal, and
 * `cursor-none-override` on `<html>` (see `globals.css`) if {@link useCursorPreference}'s
 * `effectiveStyle` is custom (persisted preference minus temporary suppress scopes). Otherwise
 * skips custom cursor chrome but keeps {@link ClickSpark} on all devices.
 *
 * Escape hatch: set `data-sq-cursor-intent` on a node (`SQ_CURSOR_INTENT_DATA_ATTRIBUTE`
 * in `smooth-cursor.tsx`) if a `!cursor-*` utility prevents hiding the OS cursor.
 * Standard Tailwind `cursor-help` maps to the custom cursor **info** badge.
 * Temporary suppress: `useSuppressCustomCursor(scopeId, active)` or overlay
 * `suppressCustomCursorScope` (does not change stored preference).
 */
export function PlatformCursors() {
	const customCursorEligible = useShowCursorStyleControls();
	const { effectiveStyle: cursorStyle } = useCursorPreference();

	useEffect(() => {
		if (!customCursorEligible || cursorStyle !== "custom") {
			return;
		}
		document.documentElement.classList.add("cursor-none-override");
		return () => {
			document.documentElement.classList.remove("cursor-none-override");
		};
	}, [customCursorEligible, cursorStyle]);

	return (
		<>
			{customCursorEligible ? (
				<>
					<CursorMessagePortalMount />
					{cursorStyle === "custom" ? (
						<SmoothCursor
							springConfig={{
								damping: 45,
								stiffness: 600,
								mass: 1,
								restDelta: 0.001,
							}}
						/>
					) : null}
				</>
			) : null}
			<ClickSpark
				mode="window"
				fullscreen
				autoContrast
				trigger="up"
				sparkColor="#fff"
				sparkSize={10}
				sparkRadius={25}
				sparkCount={8}
				duration={400}
			/>
		</>
	);
}
